import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store';
import { login } from '../../store/slices/authSlice';
import { toast } from '../../common/utils/toast';
import TempleIcon from '../../common/assets/logos/tdb.svg';
import AyyappanIcon from '../../common/assets/logos/Ayyappan.svg';
import SabariMalaTemplte from '../../common/assets/images/sabari_mala_temple.svg';
import EyeIcon from '../../common/assets/icons/eye.svg';
import EyeOffIcon from '../../common/assets/icons/eye-off.svg';
import { useClock } from '../../common/utils/clock';
import Footer from '../../common/components/Footer';

const Login: React.FC = () => {
  const clockData = useClock();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, error } = useAppSelector((state) => state.auth);
  const [showPassword, setShowPassword] = useState(false);

  const validationSchema = yup.object({
    userid: yup.string().required(t('common.required')),
    password: yup.string().required(t('common.required')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (data: { userid: string; password: string }) => {
    try {
      await dispatch(login(data)).unwrap();
      // Redirect to vanchistatus page after successful login
      navigate('/vanchistatus');
    } catch (error: any) {
      toast.showError(error?.message || 'Login failed');
    }
  };

  return (
    <div className="min-vh-100 d-flex flex-column">
      <div className="d-flex flex-grow-1">
        <div className='left-section d-flex flex-column p-5'>
          {/* <TempleIcon width="120" height="120" className="mb-4" /> */}
          <div className='d-flex justify-content-between align-items-center w-100 mb-5'>
            <TempleIcon width="95" height="95" />
            <div className='d-flex flex-column align-items-center text-primary'>
              <h6 className='fw-normal login-templeName mb-3'>SWAMY SARANAM</h6>
              <h4 className='fw-medium login-templeBoard mb-3'>TRAVANCORE DEVASWOM BOARD</h4>
              <h4 className='fw-light login-templeDeva'>SABARIMALA DEVASWOM</h4>
            </div>
            <AyyappanIcon width="95" height="95" />
          </div>
          <div className='d-flex justify-content-center align-items-center w-100'>
            <SabariMalaTemplte width="450" height="450" />
          </div>
        </div>
        <div className='right-section d-flex flex-column p-5'>
          <div className='d-flex flex-column text-primary py-2'>
            <div className='text-end'><span className='login-clockTime'>{clockData.time}</span> <span className='login-clockLabel'>{clockData.timeLabel}</span></div>
            <div className='text-end login-clockDate mt-2'>{clockData.date}</div>
          </div>
          <div className='d-flex h-100 align-items-center justify-content-center'>
            <div className='w-100'>
              <div className="mb-5">
                <h3 className="text-primary mb-0 login-formWelcome">{t('auth.login.title')}</h3>
              </div>

              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <div className="staff-field">
                  <input
                    type="text"
                    className={`form-control ${errors.userid ? 'is-invalid' : ''}`}
                    placeholder={t('auth.login.staffId')}
                    autoComplete="off"
                    {...register('userid')}
                    disabled={isLoading}
                  />
                  {errors.userid && (
                    <div className="invalid-feedback">
                      {errors.userid.message}
                    </div>
                  )}
                </div>

                <div className="staff-field position-relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                    placeholder={t('auth.login.password')}
                    autoComplete="off"
                    {...register('password')}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="btn btn-link position-absolute end-0 top-50 translate-middle-y pe-3"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                    style={{ 
                      border: 'none', 
                      background: 'none', 
                      padding: '0',
                      color: '#6c757d',
                      zIndex: 10
                    }}
                  >
                    {showPassword ? (
                      <EyeOffIcon width="20" height="20" />
                    ) : (
                      <EyeIcon width="20" height="20" />
                    )}
                  </button>
                  {errors.password && (
                    <div className="invalid-feedback">
                      {errors.password.message}
                    </div>
                  )}
                </div>

                <div className="text-center mt-5">
                  <button
                    type="submit"
                    className="btn btn-primary login-btn"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {t('common.loading')}
                      </>
                    ) : (
                      t('auth.login.submit')
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Login;
